import { css } from '@emotion/react'
import { ExternalStyles, HFlow, Icon, Theme, useTheme } from 'bold-ui'
import { usePecField } from 'components/form'
import { AccordionField } from 'components/form/field/AccordionField'
import React, { useEffect, useState } from 'react'
import { AccordionItem, AccordionItemPanel } from 'react-accessible-accordion'
import { MetaArray } from 'util/metaPath'

import { FormAccordionButton } from './FormSectionAccordionButton'

export interface FormSectionAccordionProps {
  title: string
  children: React.ReactNode
  uuid: string
  name: MetaArray<string | number>
  style?: ExternalStyles
  panelStyle?: ExternalStyles
  buttonStyle?: ExternalStyles
  buttonId?: string
  textStyle?: ExternalStyles
  dangerouslySetExpanded?: boolean
  header?: React.ReactNode
  hasError?: boolean
  dataTestid?: string
}

export function FormSectionAccordion(props: FormSectionAccordionProps) {
  const {
    title,
    children,
    name,
    uuid,
    style,
    buttonStyle,
    buttonId,
    textStyle,
    panelStyle,
    dangerouslySetExpanded,
    header,
    hasError,
    dataTestid,
  } = props

  const {
    input: { value: expandedItems },
  } = usePecField({ name: name })

  const isExpanded = Array.isArray(expandedItems) && expandedItems.length > 0

  const theme = useTheme()
  const errorColor = theme.pallete.status.danger.main
  const { errorButtonStyle, errorPanelStyle, defaultPanelStyle } = styles(theme, errorColor)

  const [isDangerouslyExpanded, setIsDangerouslyExpanded] = useState(true)

  useEffect(() => {
    setIsDangerouslyExpanded(dangerouslySetExpanded || null)
  }, [dangerouslySetExpanded])

  const handleClick = () => {
    if (dangerouslySetExpanded) {
      setIsDangerouslyExpanded(!isDangerouslyExpanded)
    }
  }

  return (
    <div css={css({ margin: '1rem 0' }, style)} data-testid={dataTestid}>
      <AccordionField name={name} allowZeroExpanded>
        <AccordionItem uuid={uuid} dangerouslySetExpanded={isDangerouslyExpanded}>
          <FormAccordionButton
            id={buttonId}
            text={title}
            style={[buttonStyle, hasError && errorButtonStyle]}
            textStyle={textStyle}
            onClick={handleClick}
            header={header}
          />
          <AccordionItemPanel css={[hasError ? errorPanelStyle : defaultPanelStyle, panelStyle]}>
            {children}
          </AccordionItemPanel>
        </AccordionItem>
      </AccordionField>

      {!isExpanded && hasError && (
        <HFlow
          alignItems='center'
          style={css`
            color: ${errorColor};
            margin-top: 0.25rem;
          `}
          hSpacing={0.5}
        >
          <Icon icon='infoCircleFilled' size={0.8} />
          Existem campos obrigatórios não preenchidos ou preenchidos de forma incorreta.
        </HFlow>
      )}
    </div>
  )
}

const styles = (theme: Theme, errorColor: string) => ({
  errorButtonStyle: css`
    border: 1px solid ${errorColor};
  `,
  errorPanelStyle: css`
    border: 1px solid ${errorColor};
    border-top: none;
  `,
  defaultPanelStyle: css`
    border: 1px solid ${theme.pallete.divider};
    border-top-width: 0;
  `,
})
